import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  ApiCheckForDuplicate,
  ApiEnumGroup,
  ApiIdentityOut,
  ApiMandatoryLevel
} from '@agunity/api-v4/build/data-contracts';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import dayjs from 'dayjs';
import { cloneDeep, isEmpty } from 'lodash';
import AgTypography from '../../../stories/Styles/AgTypography';
import { AgColor } from '../../../stories/Styles/AgColor';
import AgTextField from '../../../stories/Components/AgTextField';
import AgCheckBox from '../../../stories/Components/AgCheckBox';
import AgDropdown from '../../../stories/AgDropdown';
import AgTextArea from '../../../stories/Components/AgTextArea';
import AgButton from '../../../stories/Components/AgButton';

import {
  CustomFieldColumnType,
  CustomFieldItemType,
  MANDATORY_LEVEL,
  MEMBER_FIELD_NAME_NOTES,
  MEMBER_PARENT_FIELD_TYPE_NUMBER,
  ROOT_PATHS,
  SUCCESS_STATUS
} from '../../../Constants';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { selectLocaleOptions } from '../../../redux/slices/locales';
import { getCountries } from '../../../redux/slices/countries';
import {
  setEmailExists,
  setIsOpenViewAvatar,
  setStaff_CountryOptions,
  setNameCheck
} from '../../../redux/slices/staff';
import AgUploadAccountPhoto from '../../../stories/AgUploadFields/AgUploadAccountPhoto';
import AgDatePicker from '../../../stories/Components/CustomDropdowns/AgDatePicker';
import {
  HasOnBlurProps,
  ValidationListPersonalProps
} from '../AddStaff/AddStaff';
import {
  checkDuplicatedEmail,
  checkDuplicatedPersonName
} from '../../Users/redux';
import isEmail from 'validator/lib/isEmail';
import { ApiColumnType, ApiItemTypes } from '@agunity/api-v4';
import AgToast from '../../../stories/Components/AgToast';
import AgPin from '../../../components/AgPin';

export interface EditStaffDetails {
  setPersonalDetails?: Dispatch<SetStateAction<ApiIdentityOut | undefined>>;
  identity?: ApiIdentityOut;
  ageUnknown?: boolean;
  setAgeUnknown?: Dispatch<SetStateAction<boolean>>;
  setHasPersonalDetailsError?: Dispatch<SetStateAction<boolean>>;
  showPersonalDetailsError?: boolean;
  enumList?: ApiEnumGroup[];
  hasOnBlur?: HasOnBlurProps;
  setHasOnBlur?: Dispatch<SetStateAction<HasOnBlurProps>>;
  setValidationListPersonal?: Dispatch<
    SetStateAction<ValidationListPersonalProps>
  >;
  validationListPersonal?: ValidationListPersonalProps;
  imageURL?: string;
  // eslint-disable-next-line no-unused-vars
  onImageUpdate?: (base64?: string) => void;
  /** Disables the email field. In the future, we can add an array for disabled fields */
  disableEmail?: boolean;
}

export interface DropdownOptionsProps {
  id: number;
  name: string;
}

export interface CustomFieldGroupProps {
  groupName: string;
  data: CustomFieldProps[];
}

export interface CustomFieldProps {
  name: string;
  value: string;
  mandatoryLevel: ApiMandatoryLevel;
  fieldType: ApiColumnType;
  maxLength?: number;
  unitId?: number | null;
}

const EditStaffDetails = ({
  setPersonalDetails,
  setHasPersonalDetailsError,
  identity,
  imageURL,
  onImageUpdate,
  ageUnknown,
  setAgeUnknown,
  showPersonalDetailsError, // this is param for edit staff page, but not add new staff page
  enumList,
  hasOnBlur, // this is param for add new staff page
  setHasOnBlur, // this is param for add new staff page
  setValidationListPersonal,
  validationListPersonal,
  disableEmail,
}: EditStaffDetails) => {
  const messages = useIntl().formatMessage;
  const dispatch = useAppDispatch();
  const currentPath = window.location.pathname;
  const helpLink = useAppSelector((state) => (state.language.helpLink));
  const orgInfo = useAppSelector((state) => state.staff.addNewOrgInfo);
  const localeOptions = useAppSelector(selectLocaleOptions);
  const tagList = useAppSelector((state) => state.staff.tagList);
  const nameCheck = useAppSelector((state) => state.staff.nameCheck);
  const [customFieldGroupData, setCustomFieldGroupData] = useState<
    CustomFieldGroupProps[]
  >([]);
  const [showNameCheckToast, setShowNameCheckToast] = useState<boolean>(true);
  const flags = useAppSelector((state) => state.auth.flags);
  const isFlagShowContent = !flags.includes('UploadStaffImage');

  const [isToastOpen, setIsToastOpen] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'neutral'>('success');

  const countryOptions: DropdownOptionsProps[] =
    useAppSelector((state) => state.staff.countryOptions)?.map((country) => ({
      id: country.id ?? -1,
      name: country.name ?? ''
    })) || [];
  const formatLocaleOptions =
    (localeOptions.length &&
      localeOptions.map((item) => ({
        id: item.locale || '',
        name: item.name || ''
      }))) ||
    [];
  const localeName = formatLocaleOptions.find(
    (locale) => locale.id.toString() === identity?.locale
  );
  const notes =
    identity?.data?.find((item) => item.key === MEMBER_FIELD_NAME_NOTES)
      ?.value || '';

  const latitude = identity?.address?.location?.Y?.toString() ?? '';
  const longitude = identity?.address?.location?.X?.toString() ?? '';
  const dateOfBirth = identity?.dateOfBirth.split('T')[0] ?? '';
  let age = 0;
  if (dateOfBirth) {
    const birthDate = new Date(dateOfBirth);
    const currentDate = new Date();
    age = Math.max(currentDate.getFullYear() - birthDate.getFullYear(), 0);
  }

  let gender = '';
  switch (identity?.gender.toUpperCase()) {
    case 'M':
      gender = messages({ id: 'MALE' });
      break;
    case 'F':
      gender = messages({ id: 'FEMALE' });
      break;
    case 'N':
      gender = messages({ id: 'NONBINARY' });
      break;
    case 'O':
      gender = messages({ id: 'NOGENDER' });
      break;
  }

  const [countries, setCountries] =
    useState<DropdownOptionsProps[]>(countryOptions);
  const [selectedCountry, setSelectedCountry] = useState<{
    name: string;
    id: number;
  }>();

  const maxDate = (): Date => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 15);
    return date;
  };

  const minDate = (): Date => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 100);
    return date;
  };

  const checkDuplicatedName = async () => {
    if (currentPath !== ROOT_PATHS.ADD_MEMBER) return; // only do the check on add staff page
    try {
      const nameData: ApiCheckForDuplicate = {
        firstName: identity?.firstName,
        middleName: identity?.middleName,
        lastName: identity?.familyName
      };
      const result = await dispatch(
        checkDuplicatedPersonName(Number(orgInfo.id), nameData)
      );
      if (result.status === SUCCESS_STATUS) {
        if (result.data) {
          // result.data === true, means name is duplicated
          dispatch(
            setNameCheck({
              open: true,
              message:
                messages({ id: 'MEMBER_NAME_CHECK_MESSAGE' }) + orgInfo?.name + '.'
            })
          );
        } else {
          // else, name isn't duplicated
          dispatch(setNameCheck({ open: false, message: '' }));
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const checkEmail = async () => {
    if (!identity?.email) return;
    try {
      const result = await dispatch(checkDuplicatedEmail(identity?.email));
      if (result.status === SUCCESS_STATUS) {
        if (result.data) {
          // result.data === true, means email exists
          dispatch(setEmailExists(true));
        } else {
          // else, email doesn't exist
          dispatch(setEmailExists(false));
        }
      }
      if (setValidationListPersonal && validationListPersonal) {
        // validation
        let errorCaption: string[] = [];
        if (identity?.email && !isEmail(identity?.email)) {
          errorCaption.push(messages({ id: 'MEMBER_EMAIL_INVALID_CAPTION' }));
        }
        if (identity?.email && result.data) {
          errorCaption.push(messages({ id: 'MEMBER_EMAIL_CHECK' }));
        }
        if (identity?.email.length > 100) {
          errorCaption.push(messages({ id: 'MEMBER_EMAIL_MAX_100_CAPTION' }));
        }
        setValidationListPersonal((prevState) => ({
          ...prevState,
          email: errorCaption
        }));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const isMandatoryCustomFieldEmpty = (
    customFieldGroupData: CustomFieldGroupProps[]
  ): boolean => {
    return customFieldGroupData.some((groupData) =>
      groupData.data.some(
        (field) =>
          isEmpty(field.value) &&
          field.mandatoryLevel === MANDATORY_LEVEL.Mandatory
      )
    );
  };

  const isConditionalCustomFieldEmpty = (
    customFieldGroupData: CustomFieldGroupProps[]
  ): boolean => {
    let result = false;
    customFieldGroupData.forEach((groupData) => {
      // conditional field only affect fields inside the same parent group
      const hasEmpty = groupData.data.some(
        (field) =>
          isEmpty(field.value) &&
          field.mandatoryLevel === MANDATORY_LEVEL.Conditional
      );
      const hasValue = groupData.data.some(
        (field) =>
          !isEmpty(field.value) &&
          field.mandatoryLevel === MANDATORY_LEVEL.Conditional
      );
      if (hasEmpty && hasValue) {
        result = true;
        return;
      }
    });
    return result; // as long as one of the conditional fields has data, all the conditional fields cannot be empty
  };

  const renderWarningToast = (
    open: boolean,
    message: string,
    onClick?: () => void
  ) => {
    if (!open) return;
    return (
      <AgTypography
        style={{
          display: 'flex',
          gap: '12px',
          padding: '12px 16px',
          borderRadius: '6px',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: AgColor.link1
        }}
      >
        <AgTypography
          style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'start',
            alignItems: 'center'
          }}
        >
          <WarningAmberOutlinedIcon
            sx={{ width: '20px', height: '20px', color: AgColor.grey0 }}
          />
          <AgTypography
            contentStyle={'AP.Paragraph.Large.SemiBold'}
            message={message}
            style={{ color: AgColor.grey0 }}
          />
        </AgTypography>
        <AgTypography
          onClick={onClick}
          style={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <CloseIcon
            fontSize='small'
            sx={{ color: AgColor.grey0, cursor: 'pointer' }}
          />
        </AgTypography>
      </AgTypography>
    );
  };

  const saveProfileImage = async (base64?: string) => {
    onImageUpdate?.(base64);
  };

  useEffect(() => {
    const f = async () => {
      try {
        if (countries?.length === 0) {
          const cou = await dispatch(getCountries());
          if (cou?.data?.length) {
            const countryList = cou.data.map((country) => ({
              name: country.name ?? '',
              id: country.id ?? -1
            }));
            setCountries(countryList);
            dispatch(setStaff_CountryOptions(cou.data));
          }
        }
        if (countries.length && identity?.address?.countryId) {
          const countryObj = countries.find(
            (country) => country.id === identity?.address?.countryId
          );
          setSelectedCountry(countryObj);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    };
    f();
  }, [countries, dispatch, identity?.address?.countryId]);

  useEffect(() => {
    if (setHasPersonalDetailsError) {
      setHasPersonalDetailsError(
        (Boolean(latitude) && !longitude) ||
          (Boolean(longitude) && !latitude) ||
          !identity?.firstName ||
          // || !identity.address?.city // address becomes optional at the moment, waiting for further decision
          !selectedCountry ||
          isMandatoryCustomFieldEmpty(customFieldGroupData) ||
          isConditionalCustomFieldEmpty(customFieldGroupData)
      );
    }
  }, [
    customFieldGroupData,
    identity?.firstName,
    latitude,
    longitude,
    selectedCountry,
    setHasPersonalDetailsError
  ]);

  useEffect(() => {
    if (!tagList.length) return;
    let customFieldGroupData: CustomFieldGroupProps[] = [];
    let customFieldGroupData2: CustomFieldGroupProps[] = tagList.map((item) => {
      return {
        groupName: item.key ?? '',
        data: [
          {
            name: item.key ?? '',
            value: '',
            mandatoryLevel: item.mandatoryLevel ?? MANDATORY_LEVEL.Optional,
            fieldType: item.fieldType ?? CustomFieldColumnType.None,
            ...(item.maxLength ? { maxLength: item.maxLength } : null)
          }
        ]
      };
    });
    if (!identity?.data?.length) {
      tagList
        .filter((ta) => ta.itemType === ApiItemTypes.Account)
        .map((item) => {
          const parentField = tagList.find((tag) => tag.id === item.parentId);
          if (
            !parentField ||
            parentField.fieldType !== MEMBER_PARENT_FIELD_TYPE_NUMBER
          )
            return;
          const isparentFieldExist = customFieldGroupData2.find(
            (field) => field.groupName === parentField.key
          );

          if (isparentFieldExist) {
            isparentFieldExist.data.push({
              name: item.key ?? '',
              value: '',
              mandatoryLevel: item.mandatoryLevel ?? MANDATORY_LEVEL.Optional,
              fieldType: item.fieldType ?? CustomFieldColumnType.None,
              ...(item.maxLength ? { maxLength: item.maxLength } : null)
            });
            customFieldGroupData.push(isparentFieldExist);
          } else {
            customFieldGroupData.push({
              groupName: item.key ?? '',
              data: [
                {
                  name: item.key ?? '',
                  value: '',
                  mandatoryLevel:
                    item.mandatoryLevel ?? MANDATORY_LEVEL.Optional,
                  fieldType: item.fieldType ?? CustomFieldColumnType.None,
                  ...(item.maxLength ? { maxLength: item.maxLength } : null)
                }
              ]
            });
          }
        });
    } else {
      identity?.data?.forEach((field) => {
        const customField = tagList.find(
          (tag) =>
            tag.key?.toUpperCase() === field.key.toUpperCase() &&
            tag.itemType === CustomFieldItemType.Identity
        );
        if (!customField) return;
        const parentField = tagList.find(
          (tag) => tag.id === customField.parentId
        );
        if (
          !parentField ||
          parentField.fieldType !== MEMBER_PARENT_FIELD_TYPE_NUMBER
        )
          return;
        const isparentFieldExist = customFieldGroupData.find(
          (field) => field.groupName === parentField.key
        );

        if (isparentFieldExist) {
          isparentFieldExist.data.push({
            name: customField.key ?? '',
            value: field.value ?? '',
            mandatoryLevel:
              customField.mandatoryLevel ?? MANDATORY_LEVEL.Optional,
            fieldType: customField.fieldType ?? CustomFieldColumnType.None
          });
        } else {
          customFieldGroupData.push({
            groupName: parentField.key ?? '',
            data: [
              {
                name: customField.key ?? '',
                value: field.value ?? '',
                mandatoryLevel:
                  customField.mandatoryLevel ?? MANDATORY_LEVEL.Optional,
                fieldType: customField.fieldType ?? CustomFieldColumnType.None
              }
            ]
          });
        }
      });
    }

    const uniqueGroupNames = new Set<string>();
    const childNames = new Set<string>();

    const uniqueCustomFieldGroupData = customFieldGroupData.filter((group) => {
      if (!childNames.has(group.groupName)) {
        uniqueGroupNames.add(group.groupName);

        group.data.forEach((field) => {
          if (field.name === group.groupName) {
            childNames.add(field.name);
          }
        });

        return true;
      } else {
        return false;
      }
    });

    uniqueCustomFieldGroupData.map((gn) =>
      gn.data.map((d) => {
        if (d.fieldType === ApiColumnType.Enum) {
          const id = parseInt(d.name);
          if (!id) return;
          const enumx = enumList?.find((en) => en.id === id);
          if (enumx?.name) d.name = enumx.name;
          if (enumx?.values?.length && enumx?.values?.length > 0 && d.value) {
            d.value =
              enumx?.values.find((v) => v.id === parseInt(d.value))?.name || '';
          }
        }
      })
    );

    customFieldGroupData && setCustomFieldGroupData(customFieldGroupData);
  }, [enumList, identity?.data, tagList]);

  return (
    <AgTypography
      style={{
        padding: '24px 16px',
        display: 'grid',
        gap: '16px'
      }}
    >
      <AgTypography
        style={{
          display: 'grid',
          gap: '16px'
        }}
      >
        <AgTypography
          contentStyle={'AP.H5.UPPERCASE.Bold'}
          message={messages({ id: 'MEMBER_PERSONAL_DETAILS_NAME' })}
          style={{ color: AgColor.grey6 }}
        />

        <AgTypography
          style={{
            display: 'flex',
            alignItems: 'start',
            gap: '16px'
          }}
        >
          <AgTextField
            label={messages({ id: 'FIRSTNAME' })}
            value={identity?.firstName || ''}
            error={
              Boolean(validationListPersonal?.firstName.length) &&
              (showPersonalDetailsError || // logic for edit staff page
                (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.firstName)) // logic for add new staff page
            }
            caption={
              showPersonalDetailsError || // logic for edit staff page
              (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.firstName) // logic for add new staff page
                ? validationListPersonal?.firstName
                : ''
            }
            onChange={(e) => {
              if (setPersonalDetails && identity) {
                setPersonalDetails({ ...identity, firstName: e.target.value });
              }
              if (setValidationListPersonal && validationListPersonal) {
                // validation
                let errorCaption: string[] = [];
                if (!e.target.value) {
                  errorCaption.push(
                    messages({ id: 'MEMBER_Firstname_CANNOT_EMPTY_CAPTION' })
                  );
                }
                if (e.target.value.length > 50) {
                  errorCaption.push(
                    messages({ id: 'MEMBER_Firstname_MAX_50_CAPTION' })
                  );
                }
                setValidationListPersonal((prevState) => ({
                  ...prevState,
                  firstName: errorCaption
                }));
              }
            }}
            required
            onBlur={() => {
              setHasOnBlur &&
                setHasOnBlur((prevHasOnBlur) => ({
                  ...prevHasOnBlur,
                  firstName: true
                }));
              checkDuplicatedName();
            }}
          />
          <AgTextField
            optionalText
            label={messages({ id: 'MIDDLENAME' })}
            value={identity?.middleName || ''}
            error={
              Boolean(validationListPersonal?.middleName.length) &&
              (showPersonalDetailsError || // logic for edit staff page
                (currentPath === ROOT_PATHS.ADD_MEMBER &&
                  hasOnBlur?.middleName)) // logic for add new staff page
            }
            caption={
              showPersonalDetailsError || // logic for edit staff page
              (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.middleName) // logic for add new staff page
                ? validationListPersonal?.middleName
                : ''
            }
            onChange={(e) => {
              if (setPersonalDetails && identity) {
                setPersonalDetails({ ...identity, middleName: e.target.value });
              }
              if (setValidationListPersonal && validationListPersonal) {
                // validation
                let errorCaption: string[] = [];
                if (e.target.value.length > 50) {
                  errorCaption.push(
                    messages({ id: 'MEMBER_Middlename_MAX_50_CAPTION' })
                  );
                }
                setValidationListPersonal((prevState) => ({
                  ...prevState,
                  middleName: errorCaption
                }));
              }
            }}
            onBlur={() => {
              checkDuplicatedName();
              setHasOnBlur &&
                setHasOnBlur((prevHasOnBlur) => ({
                  ...prevHasOnBlur,
                  middleName: true
                }));
            }}
          />
          <AgTextField
            optionalText
            label={messages({ id: 'LASTNAME' })}
            value={identity?.familyName || ''}
            error={
              Boolean(validationListPersonal?.lastName.length) &&
              (showPersonalDetailsError || // logic for edit staff page
                (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.lastName)) // logic for add new staff page
            }
            caption={
              showPersonalDetailsError || // logic for edit staff page
              (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.lastName) // logic for add new staff page
                ? validationListPersonal?.lastName
                : ''
            }
            onChange={(e) => {
              if (setPersonalDetails && identity) {
                setPersonalDetails({ ...identity, familyName: e.target.value });
              }
              if (setValidationListPersonal && validationListPersonal) {
                // validation
                let errorCaption: string[] = [];
                if (e.target.value.length > 50) {
                  errorCaption.push(
                    messages({ id: 'MEMBER_LastName_MAX_50_CAPTION' })
                  );
                }
                setValidationListPersonal((prevState) => ({
                  ...prevState,
                  lastName: errorCaption
                }));
              }
            }}
            onBlur={() => {
              checkDuplicatedName();
              setHasOnBlur &&
                setHasOnBlur((prevHasOnBlur) => ({
                  ...prevHasOnBlur,
                  lastName: true
                }));
            }}
          />
        </AgTypography>
        {renderWarningToast(
          nameCheck?.open && showNameCheckToast, // on summary page, it would be open again, so we need a separated control on this page
          nameCheck?.message,
          () => {
            setShowNameCheckToast(false);
          }
        )}

        <Divider sx={{ margin: '16px 0' }} />

        <AgTypography
          contentStyle={'AP.H5.UPPERCASE.Bold'}
          message={messages({ id: 'MEMBER_PERSONAL_DETAILS_PERSONAL' })}
          style={{ color: AgColor.grey6 }}
        />
        <AgTypography
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}
        >
          <AgDatePicker
            optionalText
            label={messages({ id: 'MEMBER_PERSONAL_DETAILS_DATE_OF_BIRTH' })}
            value={ageUnknown ? dayjs('') : dayjs(dateOfBirth)}
            maxDate={dayjs(maxDate())}
            minDate={dayjs(minDate())}
            disabled={ageUnknown}
            error={
              Boolean(validationListPersonal?.dateOfBirth.length) &&
              ((!ageUnknown && showPersonalDetailsError) || // logic for edit staff page
                (!ageUnknown &&
                  currentPath === ROOT_PATHS.ADD_MEMBER &&
                  hasOnBlur?.dateOfBirth)) // logic for add new staff page
            }
            caption={
              Boolean(validationListPersonal?.dateOfBirth.length) &&
              ((!ageUnknown && showPersonalDetailsError) || // logic for edit staff page
                (!ageUnknown &&
                  currentPath === ROOT_PATHS.ADD_MEMBER &&
                  hasOnBlur?.dateOfBirth)) // logic for add new staff page
                ? validationListPersonal?.dateOfBirth
                : ''
            }
            onClose={() => {
              setHasOnBlur &&
                setHasOnBlur((prevHasOnBlur) => ({
                  ...prevHasOnBlur,
                  dateOfBirth: true
                }));
            }}
            onChange={(e) => {
              if (setPersonalDetails && identity) {
                setPersonalDetails({
                  ...identity,
                  dateOfBirth: dayjs(e).format('YYYY-MM-DDTHH:mm:ss')
                });
              }
              if (setValidationListPersonal && validationListPersonal) {
                // validation
                let errorCaption: string[] = [];
                if (!e) {
                  errorCaption.push(messages({ id: 'MEMBER_DOB_CAPTION' }));
                }
                if (dayjs().diff(dayjs(e), 'year') < 15) {
                  errorCaption.push(
                    messages({ id: 'MEMBER_DOB_UNDER_15_CAPTION' })
                  );
                } else if (dayjs().diff(dayjs(e), 'year') > 100) {
                  errorCaption.push(
                    messages({ id: 'MEMBER_DOB_OVER_100_CAPTION' })
                  );
                }
                setValidationListPersonal((prevState) => ({
                  ...prevState,
                  dateOfBirth: errorCaption
                }));
              }
            }}
            style={{
              flex: 1
            }}
            required
          />
          <AgTypography
            style={{
              flex: 1
            }}
          >
            <AgTypography
              style={{
                display: 'flex',
                gap: '24px',
                paddingLeft: '12px'
              }}
            >
              <AgCheckBox
                label={messages({ id: 'UNKNOWN' })}
                checked={ageUnknown}
                onChange={() => {
                  setAgeUnknown && setAgeUnknown((prevState) => !prevState);
                }}
              />
              <AgTypography
                contentStyle={'AP.Paragraph.Large.SemiBold'}
                style={{ color: AgColor.grey8 }}
              >
                {!ageUnknown &&
                  !isNaN(age) &&
                  messages({ id: 'MEMBER_PERSONAL_DETAILS_AGE' }) + ': ' + age}
              </AgTypography>
            </AgTypography>
          </AgTypography>
        </AgTypography>

        <AgTypography
          style={{
            display: 'flex',
            alignItems: 'start',
            gap: '16px'
          }}
        >
          <AgTypography
            style={{
              flex: 1
            }}
          >
            <AgDropdown
              inputLabel={messages({ id: 'MEMBER_PERSONAL_DETAILS_GENDER' })}
              options={[
                { id: 'M', name: messages({ id: 'MALE' }) },
                { id: 'F', name: messages({ id: 'FEMALE' }) },
                { id: 'N', name: messages({ id: 'NONBINARY' }) },
                { id: 'O', name: messages({ id: 'NOGENDER' }) }
              ]}
              value={gender}
              error={
                Boolean(validationListPersonal?.gender.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.gender)) // logic for add new staff page
              }
              caption={
                Boolean(validationListPersonal?.gender.length) &&
                ((!identity?.gender && showPersonalDetailsError) || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER &&
                    !identity?.gender &&
                    hasOnBlur?.gender)) // logic for add new staff page
                  ? validationListPersonal?.gender[0] // only accept 1 item in array
                  : ''
              }
              onBlur={() => {
                setHasOnBlur &&
                  setHasOnBlur((prevHasOnBlur) => ({
                    ...prevHasOnBlur,
                    gender: true
                  }));
              }}
              onChange={(e: string) => {
                let gender = 'O';
                switch (e.toUpperCase()) {
                  case messages({ id: 'MALE' }).toUpperCase():
                    gender = 'M';
                    break;
                  case messages({ id: 'FEMALE' }).toUpperCase():
                    gender = 'F';
                    break;
                  case messages({ id: 'NONBINARY' }).toUpperCase():
                    gender = 'N';
                    break;
                }

                if (setPersonalDetails && identity) {
                  setPersonalDetails({ ...identity, gender: gender });
                }
                if (setValidationListPersonal && validationListPersonal) {
                  // validation
                  let errorCaption: string[] = [];
                  if (!e) {
                    errorCaption.push(messages({ id: 'MEMBER_DOB_CAPTION' }));
                  }
                  setValidationListPersonal((prevState) => ({
                    ...prevState,
                    gender: errorCaption
                  }));
                }
              }}
              sx={{ mt: 2 }}
            />
          </AgTypography>
          <AgTypography
            style={{
              flex: 1
            }}
          >
            <AgDropdown
              inputLabel={messages({ id: 'MEMBER_PERSONAL_DETAILS_LANGUAGE' })}
              options={formatLocaleOptions}
              sx={{ mt: 2 }}
              value={localeName?.name || ''}
              error={
                Boolean(validationListPersonal?.language.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER &&
                    hasOnBlur?.language)) // logic for add new staff page
              }
              caption={
                Boolean(validationListPersonal?.language.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER &&
                    hasOnBlur?.language)) // logic for add new staff page
                  ? validationListPersonal?.language[0] // only 1 item would be in array
                  : ''
              }
              onBlur={() => {
                setHasOnBlur &&
                  setHasOnBlur((prevHasOnBlur) => ({
                    ...prevHasOnBlur,
                    language: true
                  }));
              }}
              onChange={(e: string) => {
                const selectedLanguage = localeOptions.find(
                  (language) => language.name === e
                );
                if (setPersonalDetails && identity && selectedLanguage) {
                  if (selectedLanguage.locale)
                    setPersonalDetails({
                      ...identity,
                      locale: selectedLanguage.locale
                    });
                }
                if (setValidationListPersonal && validationListPersonal) {
                  // validation
                  let errorCaption: string[] = [];
                  if (!selectedLanguage?.locale) {
                    errorCaption.push(
                      messages({ id: 'MEMBER_LANGUAGE_CAPTION' })
                    );
                  }
                  setValidationListPersonal((prevState) => ({
                    ...prevState,
                    language: errorCaption
                  }));
                }
              }}
            />
          </AgTypography>
        </AgTypography>
        <AgTypography
          style={{
            flex: 1
          }}
        >
          <AgUploadAccountPhoto
            imageURL={imageURL}
            saveProfileImage={saveProfileImage}
            openAvatar={() => dispatch(setIsOpenViewAvatar(true))}
            isFlagShowContent={isFlagShowContent}
          />
        </AgTypography>

        <AgTextArea
          optionalText
          label={messages({ id: 'MEMBER_PERSONAL_DETAILS_NOTES' })}
          placeholder={messages({
            id: 'MEMBER_PERSONAL_DETAILS_NOTES_PLACEHOLDER'
          })}
          value={notes}
          error={
            Boolean(validationListPersonal?.notes.length) &&
            (showPersonalDetailsError || // logic for edit staff page
              (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.notes)) // logic for add new staff page
          }
          caption={
            Boolean(validationListPersonal?.notes.length) &&
            (showPersonalDetailsError || // logic for edit staff page
              (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.notes)) // logic for add new staff page
              ? validationListPersonal?.notes[0] // only 1 item would be in array
              : ''
          }
          style={{
            maxWidth: '584px'
          }}
          onChange={(e) => {
            if (setPersonalDetails && identity) {
              const updatedIdentity = cloneDeep(identity);
              const hasNotes = updatedIdentity.data?.find(
                (item) =>
                  item.key.toUpperCase() ===
                  MEMBER_FIELD_NAME_NOTES.toUpperCase()
              );
              if (hasNotes) {
                hasNotes.value = e.target.value;
              } else {
                updatedIdentity.data?.push({
                  key: MEMBER_FIELD_NAME_NOTES,
                  value: e.target.value
                });
              }
              setPersonalDetails(updatedIdentity);
            }
            if (setValidationListPersonal && validationListPersonal) {
              // validation
              let errorCaption: string[] = [];
              if (e.target.value.length > 1200) {
                errorCaption.push(
                  messages({ id: 'MEMBER_NOTES_MAX_1200_CAPTION' })
                );
              }
              setValidationListPersonal((prevState) => ({
                ...prevState,
                notes: errorCaption
              }));
            }
          }}
          onBlur={() => {
            setHasOnBlur &&
              setHasOnBlur((prevHasOnBlur) => ({
                ...prevHasOnBlur,
                notes: true
              }));
          }}
        />
      </AgTypography>

      <AgPin
        value={identity?.pin || ''} 
        setPin={(pin: string) => {
          if(setPersonalDetails && identity){
            setPersonalDetails({ ...identity, pin });
          }
        }} 
      />

      <Divider sx={{ margin: '16px 0' }} />

      <AgTypography
        style={{
          display: 'grid',
          gap: '16px'
        }}
      >
        <AgTypography
          contentStyle={'AP.H5.UPPERCASE.Bold'}
          message={messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_TITLE' })}
          style={{ color: AgColor.grey6 }}
        />
        <AgTypography
          style={{
            display: 'flex',
            alignItems: 'start',
            gap: '16px'
          }}
        >
          <AgTypography
            style={{
              flex: 1
            }}
          >
            <AgDropdown
              inputLabel={messages({ id: 'MEMBER_PERSONAL_DETAILS_COUNTRY' })}
              options={countries}
              value={selectedCountry?.name || ''}
              error={
                Boolean(validationListPersonal?.country.length) &&
                ((showPersonalDetailsError && !selectedCountry) || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.country)) // logic for add new staff page
              }
              caption={
                Boolean(validationListPersonal?.country.length) &&
                ((showPersonalDetailsError && !selectedCountry) || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.country)) // logic for add new staff page
                  ? validationListPersonal?.country[0] // only 1 item would be in array
                  : ''
              }
              onBlur={() => {
                setHasOnBlur &&
                  setHasOnBlur((prevHasOnBlur) => ({
                    ...prevHasOnBlur,
                    country: true
                  }));
              }}
              onChange={(e: string) => {
                const selected = countries.find(
                  (country) => country.name === e
                );
                setSelectedCountry(selected);
                if (setPersonalDetails && identity) {
                  setPersonalDetails({
                    ...identity,
                    address: {
                      ...identity.address,
                      countryId: selected?.id ?? -1
                    }
                  });
                }
                if (setValidationListPersonal && validationListPersonal) {
                  // validation
                  let errorCaption: string[] = [];
                  if (!selected) {
                    errorCaption.push(
                      messages({ id: 'MEMBER_COUNTRY_CAPTION' })
                    );
                  }
                  setValidationListPersonal((prevState) => ({
                    ...prevState,
                    country: errorCaption
                  }));
                }
              }}
              sx={{ mt: 2 }}
            />
          </AgTypography>
          <AgTextField
            optionalText
            label={messages({ id: 'MEMBER_PERSONAL_DETAILS_PROVINCE' })}
            formStyle={{ flex: 1 }}
            value={identity?.address?.province || ''}
            error={
              Boolean(validationListPersonal?.province.length) &&
              (showPersonalDetailsError || // logic for edit staff page
                (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.province)) // logic for add new staff page
            }
            caption={
              Boolean(validationListPersonal?.province.length) &&
              (showPersonalDetailsError || // logic for edit staff page
                (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.province)) // logic for add new staff page
                ? validationListPersonal?.province
                : ''
            }
            onChange={(e) => {
              if (setPersonalDetails && identity) {
                setPersonalDetails({
                  ...identity,
                  address: { ...identity.address, province: e.target.value }
                });
              }
              if (setValidationListPersonal && validationListPersonal) {
                // validation
                let errorCaption: string[] = [];
                if (e.target.value.length > 50) {
                  errorCaption.push(
                    messages({ id: 'MEMBER_PROVINCE_MAX_50_CAPTION' })
                  );
                }
                setValidationListPersonal((prevState) => ({
                  ...prevState,
                  province: errorCaption
                }));
              }
            }}
            onBlur={() => {
              setHasOnBlur &&
                setHasOnBlur((prevHasOnBlur) => ({
                  ...prevHasOnBlur,
                  province: true
                }));
            }}
          />
        </AgTypography>
        <AgTypography
          style={{
            display: 'flex',
            alignItems: 'start',
            gap: '16px'
          }}
        >
          <AgTextField
            label={messages({ id: 'MEMBER_PERSONAL_DETAILS_CITY' })}
            formStyle={{ flex: 1 }}
            error={
              Boolean(validationListPersonal?.city.length) &&
              (showPersonalDetailsError || // logic for edit staff page
                (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.city)) // logic for add new staff page
            }
            caption={
              Boolean(validationListPersonal?.city.length) &&
              (showPersonalDetailsError || // logic for edit staff page
                (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.city)) // logic for add new staff page
                ? validationListPersonal?.city
                : ''
            }
            value={identity?.address?.city || ''}
            onChange={(e) => {
              if (setPersonalDetails && identity) {
                setPersonalDetails({
                  ...identity,
                  address: { ...identity.address, city: e.target.value }
                });
              }
              if (setValidationListPersonal && validationListPersonal) {
                // validation
                let errorCaption: string[] = [];
                if (e.target.value.length > 50) {
                  errorCaption.push(
                    messages({ id: 'MEMBER_CITY_MAX_50_CAPTION' })
                  );
                }
                // if (!e.target.value) { // address becomes options at the moment, waiting for further decision
                //   errorCaption.push(messages({ id: 'MEMBER_City_CAPTION' }));
                // }
                setValidationListPersonal((prevState) => ({
                  ...prevState,
                  city: errorCaption
                }));
              }
            }}
            optionalText
            onBlur={() => {
              setHasOnBlur &&
                setHasOnBlur((prevHasOnBlur) => ({
                  ...prevHasOnBlur,
                  city: true
                }));
            }}
          />
          <AgTextField
            optionalText
            label={messages({ id: 'MEMBER_PERSONAL_DETAILS_POSTAL_CODE' })}
            formStyle={{ flex: 1 }}
            value={identity?.address?.postCode || ''}
            error={
              Boolean(validationListPersonal?.postCode.length) &&
              (showPersonalDetailsError || // logic for edit staff page
                (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.postCode)) // logic for add new staff page
            }
            caption={
              Boolean(validationListPersonal?.postCode.length) &&
              (showPersonalDetailsError || // logic for edit staff page
                (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.postCode)) // logic for add new staff page
                ? validationListPersonal?.postCode
                : ''
            }
            onChange={(e) => {
              if (setPersonalDetails && identity) {
                setPersonalDetails({
                  ...identity,
                  address: { ...identity.address, postCode: e.target.value }
                });
              }
              if (setValidationListPersonal && validationListPersonal) {
                // validation
                let errorCaption: string[] = [];
                if (e.target.value.length > 20) {
                  errorCaption.push(
                    messages({ id: 'MEMBER_POSTCODE_MAX_20_CAPTION' })
                  );
                }
                setValidationListPersonal((prevState) => ({
                  ...prevState,
                  postCode: errorCaption
                }));
              }
            }}
            onBlur={() => {
              setHasOnBlur &&
                setHasOnBlur((prevHasOnBlur) => ({
                  ...prevHasOnBlur,
                  postCode: true
                }));
            }}
          />
        </AgTypography>
        <Divider sx={{ margin: '16px 0' }} />

        <AgTypography
          style={{
            display: 'grid',
            gap: '16px'
          }}
        >
          <AgTypography contentStyle={'AP.Paragraph.Small.Regular'}>
            <AgTypography
              contentStyle={'AP.Paragraph.Small.Regular'}
              style={{ color: AgColor.grey7 }}
              component={'span'}
            >
              {messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_TEXT_1' })}&nbsp;
            </AgTypography>
            <AgTypography
              contentStyle={'AP.Paragraph.Small.Link'}
              style={{ cursor: 'pointer' }}
              sx={{
                '&:hover': {
                  color: AgColor.primaryMain
                }
              }}
              component={'span'}
              onClick={() => { window.open(helpLink, '_blank'); }}
            >
              {messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_TEXT_2' })}
            </AgTypography>
            <AgTypography
              contentStyle={'AP.Paragraph.Small.Regular'}
              style={{ color: AgColor.grey7 }}
              component={'span'}
            >
              {messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_TEXT_DOT' })}
            </AgTypography>
          </AgTypography>

          <AgTypography
            style={{
              display: 'grid',
              gap: '16px'
            }}
          >
            <AgTextField
              optionalText
              label={messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_1' })}
              formStyle={{ flex: 1 }}
              value={identity?.address?.addressLine1 || ''}
              onChange={(e) => {
                if (setPersonalDetails && identity) {
                  setPersonalDetails({
                    ...identity,
                    address: {
                      ...identity.address,
                      addressLine1: e.target.value
                    }
                  });
                }
                if (setValidationListPersonal && validationListPersonal) {
                  // validation
                  let errorCaption: string[] = [];
                  if (e.target.value.length > 200) {
                    errorCaption.push(
                      messages({ id: 'MEMBER_ADDRESS_MAX_20_CAPTION' })
                    );
                  }
                  // as per 5045, address line 1 becomes optional. If it returns to mandatory again, plz recover below 3 lines
                  // if (!e.target.value) {
                  //   errorCaption.push(messages({ id: 'MEMBER_ADDRESS1_CAPTION' }));
                  // }
                  setValidationListPersonal((prevState) => ({
                    ...prevState,
                    address1: errorCaption
                  }));
                }
              }}
              error={
                Boolean(validationListPersonal?.address1.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER &&
                    hasOnBlur?.address1)) // logic for add new staff page
              }
              caption={
                Boolean(validationListPersonal?.address1.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER &&
                    hasOnBlur?.address1)) // logic for add new staff page
                  ? validationListPersonal?.address1
                  : ''
              }
              onBlur={() => {
                setHasOnBlur &&
                  setHasOnBlur((prevHasOnBlur) => ({
                    ...prevHasOnBlur,
                    address1: true
                  }));
              }}
            />
            <AgTextField
              optionalText
              label={messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_2' })}
              formStyle={{ flex: 1 }}
              value={identity?.address?.addressLine2 || ''}
              onChange={(e) => {
                if (setPersonalDetails && identity) {
                  setPersonalDetails({
                    ...identity,
                    address: {
                      ...identity.address,
                      addressLine2: e.target.value
                    }
                  });
                }
                if (setValidationListPersonal && validationListPersonal) {
                  // validation
                  let errorCaption: string[] = [];
                  if (e.target.value.length > 200) {
                    errorCaption.push(
                      messages({ id: 'MEMBER_ADDRESS_MAX_20_CAPTION' })
                    );
                  }
                  setValidationListPersonal((prevState) => ({
                    ...prevState,
                    address2: errorCaption
                  }));
                }
              }}
              error={
                Boolean(validationListPersonal?.address2.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER &&
                    hasOnBlur?.address2)) // logic for add new staff page
              }
              caption={
                Boolean(validationListPersonal?.address2.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER &&
                    hasOnBlur?.address2)) // logic for add new staff page
                  ? validationListPersonal?.address2
                  : ''
              }
              onBlur={() => {
                setHasOnBlur &&
                  setHasOnBlur((prevHasOnBlur) => ({
                    ...prevHasOnBlur,
                    address2: true
                  }));
              }}
            />
            <AgTextField
              optionalText
              label={messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_3' })}
              formStyle={{ flex: 1 }}
              value={identity?.address?.addressLine3 || ''}
              onChange={(e) => {
                if (setPersonalDetails && identity) {
                  setPersonalDetails({
                    ...identity,
                    address: {
                      ...identity.address,
                      addressLine3: e.target.value
                    }
                  });
                }
                if (setValidationListPersonal && validationListPersonal) {
                  // validation
                  let errorCaption: string[] = [];
                  if (e.target.value.length > 200) {
                    errorCaption.push(
                      messages({ id: 'MEMBER_ADDRESS_MAX_20_CAPTION' })
                    );
                  }
                  setValidationListPersonal((prevState) => ({
                    ...prevState,
                    address3: errorCaption
                  }));
                }
              }}
              error={
                Boolean(validationListPersonal?.address3.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER &&
                    hasOnBlur?.address3)) // logic for add new staff page
              }
              caption={
                Boolean(validationListPersonal?.address3.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER &&
                    hasOnBlur?.address3)) // logic for add new staff page
                  ? validationListPersonal?.address3
                  : ''
              }
              onBlur={() => {
                setHasOnBlur &&
                  setHasOnBlur((prevHasOnBlur) => ({
                    ...prevHasOnBlur,
                    address3: true
                  }));
              }}
            />
          </AgTypography>
        </AgTypography>

        <Divider sx={{ margin: '16px 0' }} />

        <AgTypography
          style={{
            display: 'grid',
            gap: '16px'
          }}
        >
          <AgTypography
            contentStyle={'AP.H5.UPPERCASE.Bold'}
            message={messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_TITLE' })}
            style={{ color: AgColor.grey6 }}
          />
          <AgTypography
            style={{
              display: 'flex',
              alignItems: 'start',
              gap: '16px'
            }}
          >
            <AgTextField
              optionalText={!Number(longitude) && !Number(latitude)}
              label={messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_LATITUDE' })}
              placeholder={messages({
                id: 'MEMBER_PERSONAL_DETAILS_GPS_LATITUDE'
              })}
              formStyle={{ flex: 1 }}
              type={'number'}
              error={
                Boolean(validationListPersonal?.latitude.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER &&
                    (hasOnBlur?.latitude || hasOnBlur?.longitude))) // logic for add new staff page
              } // if longitude is provided, latitude is mandatory
              caption={
                Boolean(validationListPersonal?.latitude.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER &&
                    (hasOnBlur?.latitude || hasOnBlur?.longitude))) // logic for add new staff page
                  ? validationListPersonal?.latitude
                  : ''
              }
              value={
                identity?.address.location?.Y &&
                identity?.address.location?.Y !== 0
                  ? identity?.address.location?.Y
                  : ''
              }
              onChange={(e) => {
                if (setPersonalDetails && identity) {
                  setPersonalDetails({
                    ...identity,
                    address: {
                      ...identity.address,
                      location: {
                        ...identity.address?.location,
                        Y: Number(e.target.value)
                      }
                    }
                  });
                }
                if (setValidationListPersonal && validationListPersonal) {
                  // validation
                  let errorCaption: string[] = [];
                  let errorCaptionAnother: string[] = [];
                  if (
                    Number(e.target.value) > 90 ||
                    Number(e.target.value) < -90
                  ) {
                    errorCaption.push(
                      messages({ id: 'MEMBER_Latitude_CAPTION_VALUE' })
                    );
                  }
                  if (
                    Boolean(Number(longitude)) &&
                    !Boolean(Number(e.target.value))
                  ) {
                    errorCaption.push(
                      messages({ id: 'MEMBER_Latitude_CAPTION_MANDATORY' })
                    );
                  }
                  if (
                    e.target.value.split('.').length === 2 &&
                    e.target.value.split('.')[1].length > 6
                  ) {
                    errorCaption.push(
                      messages({ id: 'MEMBER_Latitude_CAPTION_LENGTH' })
                    );
                  }

                  if (
                    Boolean(Number(e.target.value)) &&
                    !Boolean(Number(longitude))
                  ) {
                    errorCaptionAnother.push(
                      messages({ id: 'MEMBER_Longitude_CAPTION_MANDATORY' })
                    );
                  }
                  if (Number(longitude) > 180 || Number(longitude) < -180) {
                    errorCaptionAnother.push(
                      messages({ id: 'MEMBER_Longitude_CAPTION_VALUE' })
                    );
                  }
                  if (
                    longitude.split('.').length === 2 &&
                    longitude.split('.')[1].length > 6
                  ) {
                    errorCaptionAnother.push(
                      messages({ id: 'MEMBER_Longitude_CAPTION_LENGTH' })
                    );
                  }
                  setValidationListPersonal((prevState) => ({
                    ...prevState,
                    latitude: errorCaption,
                    longitude: errorCaptionAnother
                  }));
                }
              }}
              onBlur={() => {
                setHasOnBlur &&
                  setHasOnBlur((prevHasOnBlur) => ({
                    ...prevHasOnBlur,
                    latitude: true
                  }));
              }}
            />
            <AgTextField
              optionalText={!Number(longitude) && !Number(latitude)}
              label={messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_LONGITUDE' })}
              placeholder={messages({
                id: 'MEMBER_PERSONAL_DETAILS_GPS_LONGITUDE'
              })}
              formStyle={{ flex: 1 }}
              type={'number'}
              error={
                Boolean(validationListPersonal?.longitude.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER &&
                    (hasOnBlur?.latitude || hasOnBlur?.longitude))) // logic for add new staff page
              } // if latitude is provided, longitude is mandatory
              caption={
                Boolean(validationListPersonal?.longitude.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER &&
                    (hasOnBlur?.latitude || hasOnBlur?.longitude))) // logic for add new staff page
                  ? validationListPersonal?.longitude
                  : ''
              }
              value={
                identity?.address.location?.X &&
                identity?.address.location?.X !== 0
                  ? identity?.address.location?.X
                  : ''
              }
              onChange={(e) => {
                if (setPersonalDetails && identity) {
                  setPersonalDetails({
                    ...identity,
                    address: {
                      ...identity.address,
                      location: {
                        ...identity.address?.location,
                        X: Number(e.target.value)
                      }
                    }
                  });
                }
                if (setValidationListPersonal && validationListPersonal) {
                  // validation
                  let errorCaption: string[] = [];
                  let errorCaptionAnother: string[] = [];
                  if (
                    Number(e.target.value) > 180 ||
                    Number(e.target.value) < -180
                  ) {
                    errorCaption.push(
                      messages({ id: 'MEMBER_Longitude_CAPTION_VALUE' })
                    );
                  }
                  if (
                    Boolean(Number(latitude)) &&
                    !Boolean(Number(e.target.value))
                  ) {
                    errorCaption.push(
                      messages({ id: 'MEMBER_Longitude_CAPTION_MANDATORY' })
                    );
                  }
                  if (
                    e.target.value.split('.').length === 2 &&
                    e.target.value.split('.')[1].length > 6
                  ) {
                    errorCaption.push(
                      messages({ id: 'MEMBER_Longitude_CAPTION_LENGTH' })
                    );
                  }

                  if (
                    Boolean(Number(e.target.value)) &&
                    !Boolean(Number(latitude))
                  ) {
                    errorCaptionAnother.push(
                      messages({ id: 'MEMBER_Latitude_CAPTION_MANDATORY' })
                    );
                  }
                  if (Number(latitude) > 90 || Number(latitude) < -90) {
                    errorCaptionAnother.push(
                      messages({ id: 'MEMBER_Latitude_CAPTION_VALUE' })
                    );
                  }
                  if (
                    latitude.split('.').length === 2 &&
                    latitude.split('.')[1].length > 6
                  ) {
                    errorCaptionAnother.push(
                      messages({ id: 'MEMBER_Latitude_CAPTION_LENGTH' })
                    );
                  }
                  setValidationListPersonal((prevState) => ({
                    ...prevState,
                    longitude: errorCaption,
                    latitude: errorCaptionAnother
                  }));
                }
              }}
              onBlur={() => {
                setHasOnBlur &&
                  setHasOnBlur((prevHasOnBlur) => ({
                    ...prevHasOnBlur,
                    longitude: true
                  }));
              }}
            />
          </AgTypography>
          <AgTypography>
            <AgTextField
              optionalText
              label={messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_ALTITUDE' })}
              formStyle={{ flex: 1 }}
              type={'number'}
              value={
                identity?.address.location?.altitude &&
                identity?.address.location?.altitude !== 0
                  ? identity?.address.location?.altitude
                  : ''
              }
              error={
                Boolean(validationListPersonal?.altitude.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER &&
                    hasOnBlur?.altitude)) // logic for add new staff page
              }
              caption={
                Boolean(validationListPersonal?.altitude.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER &&
                    hasOnBlur?.altitude)) // logic for add new staff page
                  ? validationListPersonal?.altitude
                  : ''
              }
              onChange={(e) => {
                if (setPersonalDetails && identity) {
                  setPersonalDetails({
                    ...identity,
                    address: {
                      ...identity.address,
                      location: {
                        ...identity.address?.location,
                        altitude: Number(e.target.value)
                      }
                    }
                  });
                }
                if (setValidationListPersonal && validationListPersonal) {
                  // validation
                  let errorCaption: string[] = [];
                  if (Number(e.target.value) > 10000) {
                    errorCaption.push(
                      messages({ id: 'MEMBER_Altitude_MAX_VALUE' })
                    );
                  }
                  if (
                    Number(e.target.value) < 0.1 &&
                    Number(e.target.value) !== 0
                  ) {
                    errorCaption.push(
                      messages({ id: 'MEMBER_Altitude_MIN_VALUE' })
                    );
                  }
                  setValidationListPersonal((prevState) => ({
                    ...prevState,
                    altitude: errorCaption
                  }));
                }
              }}
              onBlur={(e) => {
                // onblur is needed cause if type 0 at the beginning, it will show 0.1 directly, which affect UX
                if (setPersonalDetails && identity) {
                  setPersonalDetails({
                    ...identity,
                    address: {
                      ...identity.address,
                      location: {
                        ...identity.address?.location,
                        altitude: Number(e.target.value)
                      }
                    }
                  });
                }
                setHasOnBlur &&
                  setHasOnBlur((prevHasOnBlur) => ({
                    ...prevHasOnBlur,
                    altitude: true
                  }));
              }}
            />
          </AgTypography>
          <AgButton
            label={messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_VIEW_BUTTON' })}
            disabled={!Number(longitude) || !Number(latitude)}
            variant={'outlined'}
            color={'secondary'}
            onClick={() => {
              if (!longitude || !latitude) return;
              const mapUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
              window.open(mapUrl, '_blank');
            }}
          />
        </AgTypography>

        <Divider sx={{ margin: '16px 0' }} />

        <AgTypography
          style={{
            display: 'grid',
            gap: '16px'
          }}
        >
          <AgTypography
            contentStyle={'AP.H5.UPPERCASE.Bold'}
            message={messages({ id: 'MEMBER_PERSONAL_DETAILS_CONTACT_TITLE' })}
            style={{ color: AgColor.grey6 }}
          />
          <AgTypography
            contentStyle={'AP.Paragraph.Small.Regular'}
            message={messages({ id: 'MEMBER_PERSONAL_DETAILS_CONTACT_TEXT' })}
            style={{ color: AgColor.grey7 }}
          />
          <AgTypography
            style={{
              display: 'grid',
              alignItems: 'center',
              gap: '16px'
            }}
          >
            <AgTextField
              optionalText
              label={messages({
                id: 'MEMBER_PERSONAL_DETAILS_CONTACT_INFO_PHONE'
              })}
              formStyle={{ flex: 1 }}
              value={identity?.phoneNumber || ''}
              error={
                Boolean(validationListPersonal?.phone.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.phone)) // logic for add new staff page
              }
              caption={
                Boolean(validationListPersonal?.phone.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.phone)) // logic for add new staff page
                  ? validationListPersonal?.phone
                  : ''
              }
              onChange={(e) => {
                if (setPersonalDetails && identity) {
                  setPersonalDetails({
                    ...identity,
                    phoneNumber: e.target.value
                  });
                }
                if (setValidationListPersonal && validationListPersonal) {
                  // validation
                  let errorCaption: string[] = [];
                  if (e.target.value.length > 40) {
                    errorCaption.push(
                      messages({ id: 'MEMBER_PHONE_MAX_40_CAPTION' })
                    );
                  }
                  setValidationListPersonal((prevState) => ({
                    ...prevState,
                    phone: errorCaption
                  }));
                }
              }}
              onBlur={() => {
                setHasOnBlur &&
                  setHasOnBlur((prevHasOnBlur) => ({
                    ...prevHasOnBlur,
                    phone: true
                  }));
              }}
            />
            <AgTextField
              optionalText
              label={messages({
                id: 'MEMBER_PERSONAL_DETAILS_CONTACT_INFO_EMAIL'
              })}
              formStyle={{ flex: 1 }}
              value={identity?.email || ''}
              error={
                Boolean(validationListPersonal?.email.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.email)) // logic for add new staff page
              }
              caption={
                Boolean(validationListPersonal?.email.length) &&
                (showPersonalDetailsError || // logic for edit staff page
                  (currentPath === ROOT_PATHS.ADD_MEMBER && hasOnBlur?.email)) // logic for add new staff page
                  ? validationListPersonal?.email
                  : ''
              }
              onChange={(e) => {
                if (setPersonalDetails && identity) {
                  setPersonalDetails({ ...identity, email: e.target.value });
                }
              }}
              onBlur={() => {
                checkEmail();
                setHasOnBlur &&
                  setHasOnBlur((prevHasOnBlur) => ({
                    ...prevHasOnBlur,
                    email: true
                  }));
              }}
              disabled={disableEmail}
            />
          </AgTypography>
        </AgTypography>
      </AgTypography>
      {isToastOpen && <AgToast open={isToastOpen} temporary onIconClick={() => {
        setIsToastOpen(false);
      }} severity={toastSeverity} message={toastMessage} />}
    </AgTypography>
  );
};

export default EditStaffDetails;
